import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, 
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  Moon,
  Coffee,
  Palmtree,
  Star
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyShifts = () => {
  const { user, isWorker } = useAuth();
  const { t, language } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [assignments, setAssignments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [myEmployee, setMyEmployee] = useState(null);
  const [approvedVacations, setApprovedVacations] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [settings, setSettings] = useState({ week_starts_monday: true });
  const [loading, setLoading] = useState(true);
  const [hoveredShift, setHoveredShift] = useState(null);

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    try {
      // Get my employee profile
      const myEmpRes = await axios.get(`${API_URL}/employees/me`);
      setMyEmployee(myEmpRes.data);

      // Get shift templates
      const templatesRes = await axios.get(`${API_URL}/shifts/templates`);
      setTemplates(templatesRes.data);

      // Get my shift assignments
      const assignmentsRes = await axios.get(`${API_URL}/shifts/assignments`, {
        params: { employee_id: myEmpRes.data.id }
      });
      setAssignments(assignmentsRes.data);

      // Get my approved vacations
      const vacationsRes = await axios.get(`${API_URL}/vacations/my`, {
        params: { status: 'approved' }
      });
      setApprovedVacations(vacationsRes.data);

      // Get holidays from employee's calendar
      if (myEmpRes.data.calendar_id) {
        try {
          const holidaysRes = await axios.get(`${API_URL}/calendars/${myEmpRes.data.calendar_id}/holidays`);
          setHolidays(holidaysRes.data);
        } catch {
          // Try to get from default calendar
          const calendarsRes = await axios.get(`${API_URL}/calendars`);
          const defaultCal = calendarsRes.data.find(c => c.is_default);
          if (defaultCal) {
            const holidaysRes = await axios.get(`${API_URL}/calendars/${defaultCal.id}/holidays`);
            setHolidays(holidaysRes.data);
          }
        }
      } else {
        // Get from default calendar
        try {
          const calendarsRes = await axios.get(`${API_URL}/calendars`);
          const defaultCal = calendarsRes.data.find(c => c.is_default);
          if (defaultCal) {
            const holidaysRes = await axios.get(`${API_URL}/calendars/${defaultCal.id}/holidays`);
            setHolidays(holidaysRes.data);
          }
        } catch {
          setHolidays([]);
        }
      }

      // Get settings for week start day
      try {
        const settingsRes = await axios.get(`${API_URL}/settings`);
        setSettings(settingsRes.data);
      } catch (e) {
        // Use default if settings not available
      }
    } catch (error) {
      console.error('Error fetching shift data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTemplate = (templateId) => templates.find(t => t.id === templateId);

  // Check if a date is a vacation day
  const isVacationDay = (date) => {
    if (!date) return null;
    const dateStr = formatDateString(date);
    return approvedVacations.find(v => {
      // Only show approved vacations
      if (v.status !== 'approved') return false;
      // Check if the date falls within the vacation period
      return dateStr >= v.start_date && dateStr <= v.end_date;
    });
  };

  // Check if a date is a holiday
  const isHoliday = (date) => {
    if (!date) return null;
    const dateStr = formatDateString(date);
    return holidays.find(h => h.date === dateStr);
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Adjust for week start (Monday = 0, Sunday = 6 if week starts monday)
    // (Sunday = 0, Monday = 1 if week starts sunday)
    let startingDay;
    if (settings.week_starts_monday) {
      startingDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    } else {
      startingDay = firstDay.getDay();
    }
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDateString = (date) => {
    // Use local date format to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getAssignmentForDate = (date) => {
    if (!date) return null;
    const dateStr = formatDateString(date);
    return assignments.find(a => a.date === dateStr);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const monthNames = language === 'es' 
    ? ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const dayNames = settings.week_starts_monday 
    ? (language === 'es'
        ? ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
        : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'])
    : (language === 'es'
        ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);

  const days = getDaysInMonth(currentDate);

  // Get upcoming shifts
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingShifts = assignments
    .filter(a => new Date(a.date) >= today)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);

  // Get upcoming vacations
  const upcomingVacations = approvedVacations
    .filter(v => new Date(v.end_date) >= today)
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
    .slice(0, 3);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-shifts-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">
          {language === 'es' ? 'Mis Turnos' : 'My Shifts'}
        </h1>
        <p className="text-slate-500">
          {language === 'es' 
            ? 'Calendario con tus turnos y vacaciones' 
            : 'Calendar with your shifts and vacations'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="border-slate-200 lg:col-span-2 overflow-visible">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-indigo-600" />
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToToday}>
                  {language === 'es' ? 'Hoy' : 'Today'}
                </Button>
                <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={goToNextMonth}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="overflow-visible">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day, i) => {
                const isWeekend = settings.week_starts_monday 
                  ? (i >= 5)
                  : (i === 0 || i === 6);
                return (
                  <div 
                    key={day} 
                    className={`text-center text-sm font-medium py-2 ${isWeekend ? 'text-slate-400' : 'text-slate-600'}`}
                  >
                    {day}
                  </div>
                );
              })}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 overflow-visible">
              {days.map((date, index) => {
                const assignment = getAssignmentForDate(date);
                const template = assignment ? getTemplate(assignment.shift_template_id) : null;
                const vacation = isVacationDay(date);
                const holiday = isHoliday(date);
                const isCurrentDay = isToday(date);
                const isPastDay = isPast(date);

                return (
                  <div
                    key={index}
                    className={`min-h-[80px] p-1 rounded-lg border transition-all overflow-visible ${
                      !date 
                        ? 'bg-transparent border-transparent' 
                        : vacation
                          ? 'bg-emerald-50 border-emerald-300'
                          : holiday
                            ? 'bg-amber-50 border-amber-300'
                            : isCurrentDay
                              ? 'bg-indigo-50 border-indigo-300'
                              : isPastDay
                                ? 'bg-slate-50 border-slate-100'
                                : 'bg-white border-slate-200 hover:border-indigo-200'
                    }`}
                  >
                    {date && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${
                          vacation
                            ? 'text-emerald-600'
                            : holiday
                              ? 'text-amber-600'
                              : isCurrentDay 
                                ? 'text-indigo-600' 
                                : isPastDay 
                                  ? 'text-slate-400' 
                                  : 'text-slate-700'
                        }`}>
                          {date.getDate()}
                          {holiday && <Star className="w-3 h-3 inline ml-1 text-amber-500" />}
                        </div>
                        {vacation ? (
                          <div 
                            className="rounded px-1 py-0.5 text-xs text-white truncate bg-emerald-500"
                            title={language === 'es' ? 'Vacaciones' : 'Vacation'}
                          >
                            <div className="font-medium truncate flex items-center gap-1">
                              <Palmtree className="w-3 h-3" />
                              {language === 'es' ? 'Vacaciones' : 'Vacation'}
                            </div>
                          </div>
                        ) : holiday && !template ? (
                          <div 
                            className="rounded px-1 py-0.5 text-xs text-white truncate bg-amber-500"
                            title={holiday.name}
                          >
                            <div className="font-medium truncate flex items-center gap-1">
                              <Star className="w-3 h-3" />
                              {holiday.name?.length > 10 ? holiday.name.slice(0, 10) + '...' : holiday.name}
                            </div>
                          </div>
                        ) : template ? (
                          <div 
                            className="rounded px-1 py-0.5 text-xs text-white truncate cursor-pointer relative"
                            style={{ backgroundColor: template.color || '#6366f1' }}
                            title={`${template.name}\n${language === 'es' ? 'Horario' : 'Schedule'}: ${template.start_time} - ${template.end_time}${template.break_start && template.break_end ? `\n${language === 'es' ? 'Descanso' : 'Break'}: ${template.break_start} - ${template.break_end}` : ''}${holiday ? `\n${language === 'es' ? 'Festivo' : 'Holiday'}: ${holiday.name}` : ''}`}
                            onMouseEnter={() => setHoveredShift(formatDateString(date))}
                            onMouseLeave={() => setHoveredShift(null)}
                          >
                            <div className="font-medium truncate">{template.name}</div>
                            <div className="opacity-80">{template.start_time}</div>
                            {holiday && (
                              <div className="absolute -top-1 -right-1 bg-amber-500 rounded-full p-0.5">
                                <Star className="w-2 h-2 text-white" />
                              </div>
                            )}
                            {/* Tooltip - shows on hover */}
                            {hoveredShift === formatDateString(date) && (
                              <div className="absolute z-[9999] left-0 top-full mt-1 p-2 bg-slate-900 text-white text-xs rounded-lg shadow-xl whitespace-nowrap border border-slate-700">
                                <div className="font-semibold mb-1">{template.name}</div>
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{template.start_time} - {template.end_time}</span>
                                </div>
                                {template.break_start && template.break_end && (
                                  <div className="flex items-center gap-1 mt-1 text-slate-300">
                                    <Coffee className="w-3 h-3" />
                                    <span>{language === 'es' ? 'Descanso' : 'Break'}: {template.break_start} - {template.break_end}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-300 italic">
                            {isPastDay ? '' : (language === 'es' ? 'Libre' : 'Off')}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming shifts & Legend */}
        <div className="space-y-6">
          {/* Upcoming vacations */}
          {upcomingVacations.length > 0 && (
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2 text-emerald-700">
                  <Palmtree className="w-5 h-5" />
                  {language === 'es' ? 'Vacaciones aprobadas' : 'Approved vacations'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {upcomingVacations.map((vacation) => {
                    const startDate = new Date(vacation.start_date);
                    const endDate = new Date(vacation.end_date);
                    
                    return (
                      <div 
                        key={vacation.id}
                        className="p-3 rounded-lg bg-white border border-emerald-200"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-emerald-900">
                            {startDate.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
                              day: 'numeric', 
                              month: 'short' 
                            })} - {endDate.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
                              day: 'numeric', 
                              month: 'short' 
                            })}
                          </span>
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            {vacation.days} {language === 'es' ? 'días' : 'days'}
                          </Badge>
                        </div>
                        {vacation.notes && (
                          <p className="text-xs text-emerald-600">{vacation.notes}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming shifts */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-lg">
                {language === 'es' ? 'Próximos turnos' : 'Upcoming shifts'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingShifts.length === 0 ? (
                <p className="text-sm text-slate-500">
                  {language === 'es' ? 'No tienes turnos asignados próximamente' : 'No upcoming shifts assigned'}
                </p>
              ) : (
                <div className="space-y-3">
                  {upcomingShifts.map((assignment) => {
                    const template = getTemplate(assignment.shift_template_id);
                    const date = new Date(assignment.date);
                    const isCurrentDay = isToday(date);
                    const vacation = isVacationDay(date);
                    
                    if (vacation) return null; // Skip shifts on vacation days
                    
                    return (
                      <div 
                        key={assignment.id}
                        className={`p-3 rounded-lg border ${isCurrentDay ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-medium ${isCurrentDay ? 'text-indigo-900' : 'text-slate-900'}`}>
                            {date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
                              weekday: 'short', 
                              day: 'numeric', 
                              month: 'short' 
                            })}
                          </span>
                          {isCurrentDay && (
                            <Badge className="bg-indigo-600 text-white text-xs">
                              {language === 'es' ? 'Hoy' : 'Today'}
                            </Badge>
                          )}
                        </div>
                        {template && (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: template.color || '#6366f1' }}
                            />
                            <span className="text-sm text-slate-700">{template.name}</span>
                            <span className="text-xs text-slate-500">
                              {template.start_time} - {template.end_time}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-lg">
                {language === 'es' ? 'Leyenda' : 'Legend'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {/* Vacation legend */}
                <div className="flex items-center gap-2 pb-2 border-b">
                  <div className="w-4 h-4 rounded bg-emerald-500" />
                  <span className="text-sm text-slate-700 flex items-center gap-1">
                    <Palmtree className="w-3 h-3" />
                    {language === 'es' ? 'Vacaciones' : 'Vacation'}
                  </span>
                </div>
                
                {/* Holiday legend */}
                <div className="flex items-center gap-2 pb-2 border-b">
                  <div className="w-4 h-4 rounded bg-amber-500" />
                  <span className="text-sm text-slate-700 flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    {language === 'es' ? 'Festivo' : 'Holiday'}
                  </span>
                </div>
                
                {templates.slice(0, 6).map((template) => (
                  <div key={template.id} className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: template.color || '#6366f1' }}
                    />
                    <span className="text-sm text-slate-700 flex-1">{template.name}</span>
                    <span className="text-xs text-slate-500">
                      {template.start_time}-{template.end_time}
                    </span>
                    {template.is_night_shift && (
                      <Moon className="w-3 h-3 text-slate-400" />
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-2 border-t">
                  <div className="w-4 h-4 rounded bg-slate-200" />
                  <span className="text-sm text-slate-500">
                    {language === 'es' ? 'Libre / Sin asignar' : 'Off / Not assigned'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MyShifts;
